(() => {
  if (customElements.get('product-addons')) return;

  class ProductAddons extends HTMLElement {
    connectedCallback() {
      this.form = document.forms[this.getAttribute('data-form')];
      this.currency = this.getAttribute('data-currency') || 'USD';
      this.locale = this.getAttribute('data-locale') || document.documentElement.lang || 'en';
      this.basePrice = parseInt(this.getAttribute('data-main-price'), 10) || 0;
      this.totalEl = this.querySelector('[data-addons-total]');
      this.checkboxes = Array.from(this.querySelectorAll('[data-addon]'));

      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.checkboxes.forEach((cb) => cb.addEventListener('change', this.onChange));

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit, true);
        this.form.addEventListener('variant:change', this.onVariantChange);
      }

      this.renderTotal();
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener('submit', this.onSubmit, true);
        this.form.removeEventListener('variant:change', this.onVariantChange);
      }
    }

    onVariantChange(event) {
      const variant = event?.detail?.variant;
      if (variant && typeof variant.price === 'number') {
        this.basePrice = variant.price;
        this.renderTotal();
      }
    }

    onChange() {
      this.renderTotal();
      this.dispatchBundleChange();
    }

    dispatchBundleChange() {
      if (!this.form) return;
      const items = this.checkedAddons().map((cb) => ({
        id: cb.getAttribute('data-variant-id'),
        price: parseInt(cb.getAttribute('data-price'), 10) || 0,
        quantity: 1,
      }));
      this.form.dispatchEvent(
        new CustomEvent('bundle:change', { detail: { items } })
      );
    }

    checkedAddons() {
      return this.checkboxes.filter((cb) => cb.checked);
    }

    renderTotal() {
      const addonSum = this.checkedAddons().reduce(
        (sum, cb) => sum + (parseInt(cb.getAttribute('data-price'), 10) || 0),
        0
      );
      const totalCents = this.basePrice + addonSum;
      if (this.totalEl) this.totalEl.textContent = this.formatMoney(totalCents);
    }

    formatMoney(cents) {
      const amount = cents / 100;
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(amount);
      } catch (_) {
        return `${this.currency} ${amount.toFixed(2)}`;
      }
    }

    getQuantity() {
      const qtyInput = this.form.querySelector('input[name="quantity"]');
      const q = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      return Number.isFinite(q) && q > 0 ? q : 1;
    }

    getMainVariantId() {
      const idInput = this.form.querySelector('input[name="id"]');
      return idInput ? idInput.value : null;
    }

    async onSubmit(event) {
      const picked = this.checkedAddons();
      if (picked.length === 0) return; // let theme handle normally

      const mainId = this.getMainVariantId();
      if (!mainId) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const productForm = this.form;
      const submitBtn = productForm.querySelector('[type="submit"]');
      if (submitBtn && submitBtn.hasAttribute('aria-disabled')) return;

      productForm.dispatchEvent(new Event('reset')); // no-op safe
      const quantity = this.getQuantity();

      // Collect existing properties from the form
      const formData = new FormData(productForm);
      const mainItem = { id: mainId, quantity, properties: {} };
      for (const [name, value] of formData.entries()) {
        const match = /^properties\[(.+)\]$/.exec(name);
        if (match) mainItem.properties[match[1]] = value;
      }
      if (Object.keys(mainItem.properties).length === 0) delete mainItem.properties;

      const items = [mainItem].concat(
        picked.map((cb) => ({
          id: cb.getAttribute('data-variant-id'),
          quantity,
          properties: { _addon_for: mainId },
        }))
      );

      // Collect sections the theme wants refreshed (drawer, cart icons, etc.)
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const body = {
        items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      };

      this.setButtonLoading(submitBtn, true);
      this.clearError(productForm);

      try {
        const response = await fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const parsed = await response.json();

        if (parsed.status) {
          const msg = parsed.description || parsed.message || 'Cart error';
          this.showError(productForm, msg);
          if (window.theme?.pubsub) {
            window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: mainId,
              errors: parsed.errors || parsed.description,
              message: parsed.message,
            });
          }
          return;
        }

        const cartJson = await (
          await fetch(window.theme.routes.cart_url, {
            method: 'GET',
            headers: { Accept: 'application/json' },
          })
        ).json();
        cartJson.sections = parsed.sections;

        if (window.theme?.pubsub) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: mainId,
            cart: cartJson,
          });
        }
        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', { detail: { product: parsed } })
        );

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show(submitBtn);
        } else if (
          document.body.classList.contains('template-cart') ||
          window.theme?.settings?.cartType === 'page'
        ) {
          window.location.href = window.theme.routes.cart_url;
        }
      } catch (err) {
        this.showError(productForm, 'Network error, please try again.');
        console.error(err);
      } finally {
        this.setButtonLoading(submitBtn, false);
      }
    }

    setButtonLoading(button, loading) {
      if (!button) return;
      if (loading) {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
      }
    }

    clearError(form) {
      const el = form.querySelector('.product-form__error-message');
      if (!el) return;
      el.setAttribute('hidden', '');
      el.textContent = '';
    }

    showError(form, message) {
      const el = form.querySelector('.product-form__error-message');
      if (!el) return;
      el.removeAttribute('hidden');
      el.textContent = message;
    }
  }

  customElements.define('product-addons', ProductAddons);
})();
